import React, { useCallback, useEffect, useState } from "react"
import { ToastContainer, toast } from "react-toastify"
import { request } from "../../services/request"
import { SensorsListData, SensorsListResponse, SubscriptionData, UpdateSubscriptionResponse } from "../../services/authinterface"
import Table, { TableAction } from "../../components/common/Table"
import { useDebounce } from "../../hooks/useDebounce"
import { useDispatch } from "react-redux"
import { changeloaderState } from "../../store/loaderSlice"
import { AppDispatch } from "../../store/store"
import PrimaryButton from "../../components/subComponents/PrimaryButton"
import { donwloadCSV, downloadPDF } from "../../utils/exportFunctions"
import { EditIcon, RemoveIcon, ResetIcon, ViewIcon } from "../../icons/webSvg"
import SensorModel from "./SensorModel"

const SensorsList: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const [sensors, setSensors] = useState<SensorsListData[]>([])
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [totalItems, setTotalItems] = useState(100)
    const [search, setSearch] = useState("")
    const debounceText = useDebounce(search, 500)
    const [sort, setSort] = useState("DESC")
    const [sortCol, setSortCol] = useState("createdAt")
    const [selectedRows, setSelectedRows] = React.useState<SensorsListData[]>([])
    const [sensorModel, setSensorModel] = React.useState<boolean>(false)
    const [sensorData, setSensorData] = React.useState<SensorsListData | null>(null)

    const [allOwners, setAllOwners] = useState<SubscriptionData[]>([])
    const [allLocations, setAllLocations] = useState<any>([])

    const [status, setStatus] = useState("")
    const [owner, setOwner] = useState("")
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
        start: "",
        end: "",
      })
    const [location, setLocation] = useState("")

    // For Bulk Actions
    const [changeStatus, setChangeStatus] = useState("")
    const [assignOwner, setAssignOwner] = useState("")

    const bulkActions = [
        {
            label: "Change Status",
            type: "select",
            value: changeStatus,
            onChange: (e: any) => setChangeStatus(e.target.value),
            className: "userfilter ",
            option: [
                { value: "", label: "Select Status" , disabled: true},
                { value: "1", label: "Active" },
                { value: "0", label: "Offline" },
            ],
        },
        {
            label: "Assign Owner",
            type: "select",
            value: assignOwner,
            onChange: (e: any) => setAssignOwner(e.target.value),
            className: "userfilter sensor-assign-owner",
            option: [
                { value: "", label: "Select Owner", disabled: true },
                ...allOwners.map((item) => ({
                    value: item.userId,
                    label:
                        item.first_name || item.last_name
                            ? `${item.first_name ?? ""} ${item.last_name ?? ""}`.trim()
                            : item.userAccounts?.email ?? "Unknown User",
                })),
            ],
        },
        {
            label: "Apply",
            type: "button",
            onClick: () => {
                setLocation("")
                setSearch("")
                setStatus("")
            },
            className: "resetBtn",
        },

        {
            label: "Remove",
            type: "button",
            onClick: () => {
                setLocation("")
                // setModel("")
                setSearch("")
                setStatus("")
            },
            icon: (
                <RemoveIcon />
            ),

            className: "resetBtn",
        },
        {
            label: "Move to Inventory",
            type: "button",
            onClick: () => {
                setLocation("")
                // setModel("")
                setSearch("")
                setStatus("")
            },
            icon: (
                <ResetIcon />
            ),

            className: "resetBtn",
        },
    ]

    const multipleFilters = [
        {
          label: "Status",
          type: "select",
          value: status,
          onChange: (e: any) => setStatus(e.target.value),
          className: "userfilter sensor-status sensorsList-filter",
          option: [
            { value: "", label: "All" },
            { value: "1", label: "Active" },
            { value: "0", label: "Offline" },
          ],
        },
        {
          label: "Owner",
          type: "select",
          value: owner,
          onChange: (e: any) => setOwner(e.target.value),
          className: "userfilter sensorsList-filter",
          option: [
            { value: "", label: "All" },
            ...allOwners.map((item) => ({
              value: item.userId,
              label:
                item.first_name || item.last_name
                  ? `${item.first_name ?? ""} ${item.last_name ?? ""}`.trim()
                  : item.userAccounts?.email ?? "Unknown User",
            })),
          ],
        },
        {
          label: "Location",
          type: "select",
          value: location,
          onChange: (e: any) => setLocation(e.target.value),
          className: "userfilter sensor-model sensorsList-filter",
          option: [
            { value: "", label: "All" },
            ...allLocations
              .filter((item: any) => item.location && item.location.trim() !== "")
              .map((item: any) => ({
                value: item.id,
                label: item.location,
              })),
          ],
        },
        {
          label: "Last Contact (From)",
          type: "datetime-local",
          value: dateRange.start,
          onChange: (e: any) =>
            setDateRange((prev) => ({ ...prev, start: e.target.value })),
          className: "datefilter sensorsList-filter",
        },
        {
          label: "Last Contact (To)",
          type: "datetime-local",
          value: dateRange.end,
          onChange: (e: any) =>
            setDateRange((prev) => ({ ...prev, end: e.target.value })),
          className: "datefilter sensorsList-filter",
        },
        {
          label: "Reset",
          type: "button",
          onClick: () => {
            setDateRange({ start: "", end: "" })
            setLocation("")
            setSearch("")
            setStatus("")
            setOwner("")
          },
          icon: <ResetIcon />,
          className: "resetBtn Sensors-reset sensorsList-filter",
        },
      ]

    const fetchSensorsData = useCallback(async () => {
        try {
            dispatch(changeloaderState(true))
            const params = {
                page: page,
                limit: rowsPerPage,
                search: debounceText,
                sort: sort,
                sortCol: sortCol,
                status: status,
                ownerId: owner,
                addressId: location,
                startDate: dateRange.start,
                endDate: dateRange.end
            }
            const response = await request<SensorsListResponse>({
                url: "/adminCommon/getAllSensors",
                method: "POST",
                data: params,
            })

            if (response.success) {
                setSensors(response.data)
                setTotalItems(response.total)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
        } finally {
            dispatch(changeloaderState(false))
        }
    }, [dispatch, page, rowsPerPage, location, owner, status, debounceText, sort, sortCol, dateRange])

    useEffect(() => {
        fetchSensorsData()
    }, [page, rowsPerPage, debounceText, location, sort, status, owner, fetchSensorsData, sortCol, sort, dateRange])

    const fetchOwners = useCallback(async () => {
        try {
            const params = {
                page: 1,
                limit: 100000,
                search: "",
            }
            const response = await request<UpdateSubscriptionResponse>({
                url: "/adminCustomers/allOwners",
                method: "POST",
                data: params,
            })

            if (response.success) {
                setAllOwners(response.data)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
        }
    }, [])

    useEffect(() => {
        fetchOwners()
    }, [fetchOwners])

    const fetchLocations = useCallback(async () => {
        try {
            const response = await request<any>({
                url: "/adminCommon/getAllLocations",
                method: "GET",
            })

            if (response.success) {
                setAllLocations(response.data)
            } else {
                toast.error("Failed to fetch: " + response.message)
            }
        } catch (error: any) {
            toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`)
        }
    }, [])

    useEffect(() => {
        fetchLocations()
    }, [fetchLocations])

    useEffect(() => {
        if (debounceText) {
            setPage(1)
        }
    }, [debounceText])

    const columns = [
        {
            name: "Device ID",
            value: (Sensor: SensorsListData) =>
                Sensor?.sensor_id,
            id: "sensor_id",
            className: "long-text-devices"
        },
        {
            name: "Status",
            value: (Sensor: SensorsListData) => (
                <span className="d-flex align-items-center">
                    <span
                        // className={`me-2 rounded-circle ${Sensor.status !== "0" ? "bg-success" : "bg-danger"}`}
                        className={`me-2 rounded-circle ${
                            Sensor?.status === "2"
                            ? "bg-success"
                            : Sensor?.status === "1"
                            ? "bg-warning"
                            : Sensor?.status === "3"
                            ? "bg-con-error"
                            : "bg-danger"
                        }`}
                        style={{ width: "10px", height: "10px", display: "inline-block" }}
                    ></span>
                    {/* <span>{Sensor.status === "0" ? "Offline" : "Active"}</span> */}
                    {Sensor?.status === "2"
                      ? "Active"
                      : Sensor?.status === "1"
                      ? "ALERT"
                      : Sensor?.status === "3"
                      ? "Connection Error"
                      : "Offline"}
                </span>
            ),
            id: "Status",
        },
        {
            name: "Last Contact",
            value: (Sensor: SensorsListData) =>
                Sensor?.testedAt ? new Date(Sensor?.testedAt).toLocaleString("en-CA", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false
                }).replace(",", "") : "-",  // remove comma after date
            id: "testedAt"
        },
        {
            name: "Owner / Account",
            value: (Sensor: SensorsListData) =>
                !Sensor?.createdlocationUserAccount?.first_name && !Sensor?.createdlocationUserAccount?.last_name
                    ? "-"
                    : (Sensor?.createdlocationUserAccount?.first_name ? Sensor?.createdlocationUserAccount?.first_name : "") +
                    " " +
                    (Sensor?.createdlocationUserAccount?.last_name ? Sensor?.createdlocationUserAccount?.last_name : ""),

            id: "createdlocationUserAccount",
            className: "long-text-devices"
        },
        {
            name: "Location",
            value: (Sensor: SensorsListData) =>
                Sensor?.locationsSensor?.address,
            id: "address",
            className: "long-text-devices"
        },
        {
            name: "Model",
            value: (Sensor: SensorsListData) =>
                // Sensor?.name,
                "G3-SBS-R2D",
            id: "model",
        },
        {
            name: "Nickname",
            value: (Sensor: SensorsListData) =>
                Sensor?.name,
            id: "name",
            className: "long-text-devices"
        },
    ]

    const action: TableAction[] = [
        {
            label: "",
            // onClick: (row: SensorsListData) => {
            //     navigate(`/location/device-details/${row.id}`)
            // },
            isRedirect: true,
            link: (row: SensorsListData) => `/location/device-details/${row.id}`,
            className: "table-btn-2",
            icon: (
                <ViewIcon />
            ),
        },
        {
            label: "",
            onClick: (row: SensorsListData) => {
                setSensorData(row)
                setSensorModel(true)
            },
            className: "table-btn-2",
            icon: (
                <EditIcon />
            ),
        },
    ]

    const fetchAllSensors = async () => {
        try {
            const params = {
                page: 1,
                search: debounceText,
                limit: 100000,
                sort: sort,
                sortCol: sortCol,
            };

            const response = await request<SensorsListResponse>({
                url: "/adminCommon/getAllSensors", // Use the correct URL
                method: "POST",
                data: params,
            });

            if (response.success) {
                return response.data;
            } else {
                toast.error("Failed to fetch all Sensors: " + response.message);
                return [];
            }
        } catch (error: any) {
            toast.error(`${error?.response?.data?.message ||
        error?.message ||
        "Something went wrong"}`);
            return [];
        }
    };

    const exportCSV = async () => {
        const allData = await fetchAllSensors();

        if (!allData.length) {
            toast.error("No data available to export.");
            return;
        }

        // Custom headers for Sensors export
        const headers = [
            "Sr No.",
            "Device ID",
            "Status",
            "Last Contact",
            "Owner / Account",
            "Location",
            "Model",
        ];

        const rows = allData.map((sensor: any, index: number) => [
            index + 1,
            sensor?.sensor_id || "-",
            sensor?.status === "1" ? "Active" : "Inactive",
            sensor?.testedAt
                ? new Date(sensor?.testedAt).toLocaleString("en-CA", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                }).replace(",", "")
                : "-",
            !sensor?.createdlocationUserAccount?.first_name &&
                !sensor?.createdlocationUserAccount?.last_name
                ? "-"
                : `${sensor?.createdlocationUserAccount?.first_name || ""} ${sensor?.createdlocationUserAccount?.last_name || ""
                }`,
            sensor?.locationsSensor?.address || "-",
            sensor?.name || "-",
        ]);

        donwloadCSV(headers, rows, "Sensors.csv");
    };

    const exportPDF = async () => {
        const allData = await fetchAllSensors();

        if (!allData.length) {
            toast.error("No data available to export.");
            return;
        }

        const headers = [
            "Sr No.",
            "Device ID",
            "Status",
            "Last Contact",
            "Owner / Account",
            "Location",
            "Model",
        ];

        const rows = allData.map((sensor: any, index: number) => [
            index + 1,
            sensor?.sensor_id || "-",
            sensor?.status === "1" ? "Active" : "Inactive",
            sensor?.testedAt
                ? new Date(sensor?.testedAt).toLocaleString("en-CA", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                }).replace(",", "")
                : "-",
            !sensor?.createdlocationUserAccount?.first_name &&
                !sensor?.createdlocationUserAccount?.last_name
                ? "-"
                : `${sensor?.createdlocationUserAccount?.first_name || ""} ${sensor?.createdlocationUserAccount?.last_name || ""
                }`,
            sensor?.locationsSensor?.address || "-",
            sensor?.name || "-",
        ]);

        downloadPDF(headers, rows, "Sensors");
    };


    return (
        <>
          <section className=' dashboard-section dashboard-item '>
                <ToastContainer position='top-right' autoClose={2000} theme='light' />

                <div className='container'>
                    <div className='row'>
                        <div className='col-12'>
                            <div className='dashboard-table-title-section'>
                                <h2>Sensors</h2>
                                <div className='dashboard-table-title-right-side d-flex gap-2'>
                                    <PrimaryButton
                                        label='Export CSV'
                                        className='max-w-max-content'
                                        onClick={exportCSV}
                                    />
                                    <PrimaryButton
                                        label='Export PDF'
                                        className='max-w-max-content'
                                        onClick={exportPDF}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='col-12 mt-4'>
                            <div className='table-container-main'>
                                <div className='table-details-section'></div>
                                <Table
                                    rowData={sensors}
                                    columns={columns}
                                    action={action}
                                    page={page}
                                    setPage={setPage}
                                    rowsPerPage={rowsPerPage}
                                    setRowsPerPage={setRowsPerPage}
                                    totalItems={totalItems}
                                    setSortColumn={setSortCol}
                                    setSortOrder={setSort}
                                    sortColumn={sortCol}
                                    sortOrder={sort}
                                    isSearch
                                    search={search}
                                    setSearch={setSearch}
                                    multipleFilters={multipleFilters}
                                    {...(selectedRows.length > 0 && { bulkActions })}
                                    setSelected={setSelectedRows}
                                />
                            </div>
                        </div>
                    </div>
                </div>
          </section>
          <SensorModel show={sensorModel} sensorData={sensorData} onClose={() => {
            setSensorData(null)
            setSensorModel(false)
          }} fetchSensorsData={fetchSensorsData}/>
        </>
    )
}

export default SensorsList
